import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Type, Volume2, Globe, Users } from 'lucide-react';
import { Button } from './ui/button';

const PreferencesPanel = ({ isOpen, onClose }) => {
  const { 
    language, setLanguage,
    ageCategory, setAgeCategory,
    fontSize, setFontSize,
    audioSpeed, setAudioSpeed,
    t 
  } = useLanguage();

  const languages = [
    { code: 'en', native: 'English' },
    { code: 'tc', native: '繁體中文' },
    { code: 'sc', native: '简体中文' },
  ];

  const ageCategories = [
    { code: 'children', label: { en: 'Children', tc: '兒童', sc: '儿童' } },
    { code: 'adult', label: { en: 'Adult', tc: '成人', sc: '成人' } },
    { code: 'elder', label: { en: 'Elder', tc: '長者', sc: '长者' } },
  ];

  const fontSizes = [
    { value: 'small', label: { en: 'Small', tc: '小', sc: '小' } },
    { value: 'medium', label: { en: 'Medium', tc: '中', sc: '中' } },
    { value: 'large', label: { en: 'Large', tc: '大', sc: '大' } },
  ];

  const audioSpeeds = [
    { value: 0.8, label: { en: 'Slow', tc: '慢', sc: '慢' } },
    { value: 1.0, label: { en: 'Normal', tc: '正常', sc: '正常' } },
    { value: 1.2, label: { en: 'Fast', tc: '快', sc: '快' } },
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        data-testid="preferences-backdrop"
      />

      {/* Panel */}
      <div 
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
        data-testid="preferences-panel"
      >
        <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-bold">
            {t({ en: 'Preferences', tc: '偏好設定', sc: '偏好设置' })}
          </h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            data-testid="close-preferences"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-8">
          {/* Language */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Globe className="w-4 h-4" />
              <span>{t({ en: 'Language', tc: '語言', sc: '语言' })}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {languages.map((lang) => (
                <Button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  variant={language === lang.code ? 'default' : 'outline'}
                  className="h-12"
                  data-testid={`pref-lang-${lang.code}`}
                >
                  {lang.native}
                </Button>
              ))}
            </div>
          </div>

          {/* Age Category */}
          {/* <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>{t({ en: 'Age Category', tc: '年齡類別', sc: '年龄类别' })}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {ageCategories.map((age) => (
                <Button
                  key={age.code}
                  onClick={() => setAgeCategory(age.code)}
                  variant={ageCategory === age.code ? 'default' : 'outline'}
                  className="h-12"
                  data-testid={`pref-age-${age.code}`}
                >
                  {age.label[language]}
                </Button>
              ))}
            </div>
          </div> */}

          {/* Font Size */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Type className="w-4 h-4" />
              <span>{t({ en: 'Text Size', tc: '文字大小', sc: '文字大小' })}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {fontSizes.map((size) => (
                <Button
                  key={size.value}
                  onClick={() => setFontSize(size.value)}
                  variant={fontSize === size.value ? 'default' : 'outline'}
                  className="h-12"
                  data-testid={`pref-font-${size.value}`}
                >
                  {size.label[language]}
                </Button>
              ))}
            </div>
            {/* Preview */}
            <div className="p-4 bg-muted rounded-lg">
              <p className={`${fontSize === 'small' ? 'text-sm' : fontSize === 'large' ? 'text-xl' : 'text-base'}`}>
                {t({ en: 'Preview text', tc: '預覽文字', sc: '预览文字' })}
              </p>
            </div>
          </div>

          {/* Audio Speed */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Volume2 className="w-4 h-4" />
              <span>{t({ en: 'Audio Speed', tc: '音訊速度', sc: '音频速度' })}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {audioSpeeds.map((speed) => (
                <Button
                  key={speed.value}
                  onClick={() => setAudioSpeed(speed.value)}
                  variant={audioSpeed === speed.value ? 'default' : 'outline'}
                  className="h-12"
                  data-testid={`pref-speed-${speed.value}`}
                >
                  {speed.label[language]}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Done Button */}
        <div className="sticky bottom-0 bg-white border-t border-border p-4">
          <Button
            onClick={onClose}
            className="w-full h-14 text-lg"
            data-testid="preferences-done"
          >
            {t({ en: 'Done', tc: '完成', sc: '完成' })}
          </Button>
        </div>
      </div>
    </>
  );
};

export default PreferencesPanel;
